<template>
  <div class="employee-details-root">
    <div :class="mainClassObject">
      <v-container class="employee-details pa-0" fluid>
        <v-card class="hero-card" variant="flat">
          <div class="hero-left">
            <v-avatar size="72" class="hero-avatar">
              {{ initials }}
            </v-avatar>
            <div>
              <div class="hero-name d-flex align-center flex-wrap">
                <span class="mr-2">{{ fullName }}</span>
                <v-chip v-if="employee.status === 1" color="success" variant="elevated">
                  {{ t("employeeDetails.hero.active") }}
                </v-chip>
                <v-chip v-else color="grey" variant="outlined">
                  {{ t("employeeDetails.hero.inactive") }}
                </v-chip>
              </div>
              <div class="hero-meta">
                <div><strong>{{ t("employeeDetails.hero.position") }}</strong> · {{ employee.position || '—' }}</div>
                <div><strong>{{ t("employeeDetails.hero.company") }}</strong> · {{ employee.company_name || '—' }}</div>
                <div v-if="employee.role"><strong>{{ t("employeeDetails.hero.role") }}</strong> · {{ employee.role }}</div>
                <div v-if="employee.manager_full_name"><strong>{{ t("employeeDetails.hero.manager") }}</strong> · {{ employee.manager_full_name }}</div>
              </div>
            </div>
          </div>
          <div class="hero-actions">
            <v-btn
              v-if="allowHolidayRequests"
              color="primary"
              variant="tonal"
              @click="openRequestDialog()"
              :disabled="!employee.company_id"
            >
              <v-icon start>mdi-beach</v-icon>
              {{ t("employeeDetails.hero.addHoliday") }}
            </v-btn>
          </div>
        </v-card>

        <v-card
          v-if="showTaskNotifications"
          class="info-card notification-card mt-4"
          outlined
        >
          <v-card-text class="notification-content">
            <div class="notification-left">
              <v-icon size="20" color="primary" class="mr-2">mdi-bell-badge-outline</v-icon>
              <div class="notification-titles">
                <div class="notification-title">{{ t("employeeDetails.notifications.title") }}</div>
                <div class="notification-subtitle">{{ t("employeeDetails.notifications.subtitle") }}</div>
              </div>
            </div>
            <div class="notification-stats">
              <v-chip class="notif-chip" color="#E6EEF8" text-color="#0F172A" variant="flat">
                <v-icon size="16" class="mr-1">mdi-format-list-checks</v-icon>
                {{ t("employeeDetails.notifications.assigned") }}: {{ taskNotifications.total }}
              </v-chip>
              <v-chip
                class="notif-chip"
                :color="taskNotifications.overdue > 0 ? '#FDECEC' : '#E6EEF8'"
                :text-color="taskNotifications.overdue > 0 ? '#B71C1C' : '#0F172A'"
                variant="flat"
              >
                <v-icon size="16" class="mr-1">mdi-alert-circle-outline</v-icon>
                {{ t("employeeDetails.notifications.overdue") }}: {{ taskNotifications.overdue }}
              </v-chip>
              <v-btn
                color="primary"
                variant="elevated"
                class="ml-2"
                @click="openChecklistFromNotification"
              >
                {{ t("employeeDetails.notifications.open") }}
              </v-btn>
            </div>
          </v-card-text>
        </v-card>

        <v-card
          v-if="showApprovalsCard"
          class="info-card mt-4 approval-summary-card"
          outlined
        >
          <v-card-title class="info-title d-flex align-center">
            <v-icon size="18" class="mr-2">mdi-account-check-outline</v-icon>
            {{ t("employeeDetails.approvals.title") }}
            <v-spacer></v-spacer>
            <v-chip color="primary" variant="elevated">
              {{ filteredApprovalRequests.length }} {{ t("employeeDetails.approvals.pendingCount") }}
            </v-chip>
          </v-card-title>
          <v-divider></v-divider>
          <v-card-text>
            <v-alert
              v-if="isProcessingApprovalToken"
              type="info"
              density="comfortable"
              class="mb-3"
            >
              {{ t("employeeDetails.approvals.processingToken") }}
            </v-alert>
            <div v-if="loadingApprovals" class="d-flex justify-center py-4">
              <v-progress-circular indeterminate color="primary" size="24"></v-progress-circular>
            </div>
            <v-alert
              v-else-if="approvalsError"
              type="error"
              density="comfortable"
              class="mb-3"
            >
              {{ approvalsError }}
            </v-alert>
            <div
              v-else-if="!filteredApprovalRequests.length && !loadingApprovals"
              class="text-caption text-medium-emphasis"
            >
              {{ t("employeeDetails.approvals.none") }}
            </div>
            <div v-else class="approval-list">
              <div
                v-for="approval in filteredApprovalRequests"
                :key="approval.id"
                class="approval-item"
              >
                <div class="approval-item__row">
                  <div>
                    <div class="approval-item__title">
                      {{ approval.employee_full_name || t("employeeDetails.approvals.unknownEmployee") }}
                    </div>
                    <div class="approval-item__subtitle">
                      {{ formatDate(approval.start_date) }} – {{ formatDate(approval.end_date) }}
                      · {{ formatNumber(approval.working_days_requested || 0) }} {{ t("employeeDetails.approvals.days") }}
                    </div>
                  </div>
                  <v-chip
                    size="x-small"
                    :color="approvalChipColor(approval)"
                  >
                    {{ approvalChipLabel(approval) }}
                  </v-chip>
                </div>
                <div class="approval-item__meta-row">
                  <div class="approval-item__meta">
                    <div
                      v-if="approvalInfoText(approval)"
                      class="approval-item__info text-caption text-medium-emphasis mb-1"
                    >
                      {{ approvalInfoText(approval) }}
                    </div>
                    <div v-if="approval.notes" class="approval-item__note-chip" :title="approval.notes">
                      <v-icon size="14" class="mr-1" color="primary">mdi-comment-text-outline</v-icon>
                      {{ approval.notes }}
                    </div>
                    <span v-else class="approval-item__note-placeholder">
                      {{
                        approval.pending_action === 'cancel'
                          ? t("employeeDetails.approvals.noCancelReason")
                          : t("employeeDetails.approvals.noReason")
                      }}
                    </span>
                  </div>
                  <div class="approval-item__actions">
                    <v-btn
                      size="small"
                      color="success"
                      variant="flat"
                      density="comfortable"
                      :loading="approvalActionLoading(approval.id)"
                      :disabled="approvalActionLoading(approval.id)"
                      @click="handleApprovalAction(approval, 'approved')"
                    >
                      <v-icon start size="18">mdi-check</v-icon>
                      {{ approveButtonLabel(approval) }}
                    </v-btn>
                    <v-btn
                      size="small"
                      color="error"
                      variant="text"
                      density="comfortable"
                      :loading="approvalActionLoading(approval.id)"
                      :disabled="approvalActionLoading(approval.id)"
                      @click="handleApprovalAction(approval, 'rejected')"
                    >
                      <v-icon start size="18">mdi-close</v-icon>
                      {{ rejectButtonLabel(approval) }}
                    </v-btn>
                  </div>
                </div>
              </div>
            </div>
          </v-card-text>
        </v-card>

        <v-row class="mt-4" dense>
          <!-- Left column -->
          <v-col cols="12" md="3" class="column">
            <v-card outlined class="info-card">
              <v-card-title class="info-title">
                <v-icon size="18" class="mr-2">mdi-account</v-icon>
                {{ t("employeeDetails.sections.personal") }}
              </v-card-title>
              <v-divider></v-divider>
              <v-card-text>
                <div
                  v-for="item in personalInfo"
                  :key="item.label"
                  class="info-row"
                  :class="{ clickable: item.clickable }"
                  @click="item.click && item.click()"
                >
                  <v-icon v-if="item.icon" size="16" class="mr-2">{{ item.icon }}</v-icon>
                  <strong>{{ item.label }}:</strong>
                  <span>{{ item.value }}</span>
                </div>
              </v-card-text>
            </v-card>
          </v-col>

          <!-- Middle column -->
          <v-col cols="12" md="6" class="column">
            <v-card outlined class="info-card">
              <v-card-title class="info-title">
                <v-icon size="18" class="mr-2">mdi-briefcase</v-icon>
                {{ t("employeeDetails.sections.professional") }}
              </v-card-title>
              <v-divider></v-divider>
              <v-card-text>
                <div
                  v-for="item in professionalInfo"
                  :key="item.label"
                  class="info-row"
                >
                  <v-icon v-if="item.icon" size="16" class="mr-2">{{ item.icon }}</v-icon>
                  <strong>{{ item.label }}:</strong>
                  <span>{{ item.value }}</span>
                </div>
              </v-card-text>
            </v-card>

            <v-card v-if="showTopBalanceCard" outlined class="info-card mt-4">
              <v-card-title class="info-title d-flex align-center">
                <v-icon size="18" class="mr-2">mdi-beach</v-icon>
                {{ t("employeeDetails.balance.title") }}
                <v-spacer></v-spacer>
              </v-card-title>
              <v-divider></v-divider>
              <v-card-text>
                <div class="balance-grid">
                  <div>
                    <div class="text-caption">{{ t("employeeDetails.balance.annual") }}</div>
                    <div class="text-h6">{{ formatNumber(displayBalance.annual) }}</div>
                  </div>
                  <div>
                    <div class="text-caption">{{ t("employeeDetails.balance.totalAccrued") }}</div>
                    <div class="text-h6">{{ formatNumber(displayBalance.totalAccrued) }}</div>
                  </div>
                  <div>
                    <div class="text-caption">{{ t("employeeDetails.balance.taken") }}</div>
                    <div class="text-h6 text-error">{{ formatNumber(displayBalance.totalTaken) }}</div>
                  </div>
                  <div>
                    <div class="text-caption">{{ t("employeeDetails.balance.remaining") }}</div>
                    <div class="text-h6 text-success">{{ formatNumber(displayBalance.remaining) }}</div>
                  </div>
                </div>
              </v-card-text>
            </v-card>

          </v-col>

          <!-- Right column -->
          <v-col cols="12" md="3" class="column">
            <v-card v-if="showFluxSummary" outlined class="info-card">
              <v-card-title class="info-title">
                <v-icon size="18" class="mr-2">mdi-calendar-star</v-icon>
                {{ t("employeeDetails.flow.title") }}
              </v-card-title>
              <v-divider></v-divider>
              <v-card-text>
                <v-tabs v-model="activeVacationTab" density="compact" color="primary" class="vacation-tabs">
                  <v-tab value="requests">{{ t("employeeDetails.flow.tabs.requests") }}</v-tab>
                  <v-tab value="stats">{{ t("employeeDetails.flow.tabs.stats") }}</v-tab>
                </v-tabs>
                <v-window v-model="activeVacationTab" class="mt-3">
                  <v-window-item value="requests">
                    <div v-if="!visibleHolidayRequests.length" class="text-caption text-medium-emphasis">
                      {{ t("employeeDetails.flow.requestsEmpty") }}
                    </div>
                    <div v-for="item in visibleHolidayRequests" :key="item.id" class="request-card">
                      <div class="d-flex justify-space-between align-center mb-1">
                        <v-chip size="x-small" :color="statusChipColor(item.status)">
                          {{ statusLabel(item.status) }}
                        </v-chip>
                        <span class="text-caption">{{ formatNumber(getWorkingDays(item)) }} {{ t("employeeDetails.flow.days") }}</span>
                      </div>
                      <div class="request-title">{{ item.holiday_type_name || lookupHolidayType(item.holiday_type_id) || t("employeeDetails.info.unknownHolidayType") }}</div>
                      <div class="text-caption mb-1">{{ formatDate(item.start_date) }} – {{ formatDate(item.end_date) }}</div>
                      <div class="text-caption mb-2" v-if="item.notes">{{ item.notes }}</div>
                    </div>
                    <div v-if="holidayRequests.length > 2" class="text-center mt-2">
                      <v-btn size="small" variant="text" color="primary" @click="goToMyHolidays">
                        {{ t("employeeDetails.flow.seeAll") }}
                      </v-btn>
                    </div>
                  </v-window-item>

                  <v-window-item value="stats">
                    <v-card variant="outlined" class="pa-3 mb-2">
                      <div class="text-caption">{{ t("employeeDetails.flow.approvedYTD") }}</div>
                      <div class="text-h6">{{ formatNumber(statistics.approvedDaysYTD) }}</div>
                    </v-card>
                    <v-card variant="outlined" class="pa-3 mb-2">
                      <div class="text-caption">{{ t("employeeDetails.flow.remainingDays") }}</div>
                      <div class="text-h6 text-success">{{ formatNumber(statistics.remainingDays) }}</div>
                    </v-card>
                    <v-card variant="outlined" class="pa-3">
                      <div class="text-caption mb-1">{{ t("employeeDetails.flow.usage") }}</div>
                      <v-progress-linear :model-value="statistics.usagePercent" color="primary" height="12" rounded>
                        <template #default>
                          <span class="text-caption">{{ statistics.usagePercent.toFixed(0) }}%</span>
                        </template>
                      </v-progress-linear>
                    </v-card>
                  </v-window-item>
                </v-window>
              </v-card-text>
            </v-card>

          </v-col>
        </v-row>

        <v-row v-if="subordinateEmployees.length" class="mt-4">
          <v-col cols="12">
            <v-card outlined class="info-card subordinate-section">
              <v-card-title class="info-title">
                <v-icon size="18" class="mr-2">mdi-account-group</v-icon>
                {{ t("employeeDetails.subordinate.title") }}
              </v-card-title>
              <v-divider></v-divider>
              <v-card-text>
                <v-row>
                  <v-col cols="12" md="4" v-for="sub in subordinateEmployees" :key="sub.id">
                    <v-card outlined class="subordinate-card" @dblclick="openSubordinateDetails(sub.id)">
                      <div class="d-flex align-center mb-3">
                        <v-avatar color="#E0E7FF" class="mr-3">
                          {{ (sub.first_name?.[0] || '') + (sub.last_name?.[0] || '') }}
                        </v-avatar>
                        <div>
                          <div class="text-subtitle-1 font-weight-medium">{{ sub.first_name }} {{ sub.last_name }}</div>
                      <div class="text-caption text-medium-emphasis">{{ sub.position || '—' }}</div>
                    </div>
                  </div>
                      <div class="text-caption mb-3">
                        {{ t("employeeDetails.subordinate.pending") }}: {{ subordinatePendingRequests.filter(r => r.employee_id === sub.id).length }}
                      </div>
                      <div class="d-flex justify-space-between">
                        <v-btn size="x-small" variant="text" color="primary" @click="openSubordinateDetails(sub.id)">
                          {{ t("employeeDetails.subordinate.viewProfile") }}
                        </v-btn>
                        <v-btn
                          size="x-small"
                          variant="outlined"
                          color="primary"
                          @click="goToSubordinateApprovals(sub.id)"
                          :disabled="!subordinatePendingRequests.some(r => r.employee_id === sub.id)"
                        >
                          {{ t("employeeDetails.subordinate.approvePending") }}
                        </v-btn>
                      </div>
                    </v-card>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <!-- Dialogs for contacts and forms -->
        <HolidayRequestPopUp
          v-if="allowHolidayRequests"
          v-model="requestDialog"
          :loading="requestDialogLoading"
          :request-form="requestForm"
          :holiday-type-options="holidayTypeOptions"
          :status-options="statusOptions"
          :request-range-menu="requestRangeMenu"
          :range-date-model="rangeDateModel"
          :allowed-calendar-dates="allowedCalendarDates"
          :calendar-events="calendarEvents"
          :on-calendar-range-change="onCalendarRangeChange"
          :on-range-pointer-down="handleRangePointerDown"
          :on-range-field-focus="handleRangeFieldFocus"
          :on-range-keyboard-open="handleRangeKeyboardOpen"
          :on-range-control-click="handleRangeControlClick"
          :hide-status="true"
          :hide-approved="true"
          @update:request-range-menu="(value) => (requestRangeMenu = value)"
          @cancel="requestDialog = false"
          @submit="submitHolidayRequest"
        />

        <v-dialog v-model="balanceDialog" max-width="560px">
          <v-card>
            <v-card-title>
              {{ balanceDialogMode === 'edit' ? t("employeeDetails.balanceDialog.editTitle") : t("employeeDetails.balanceDialog.updateTitle") }}
            </v-card-title>
            <v-card-text>
              <v-form @submit.prevent="submitBalance">
                <v-container>
                  <v-row>
                    <v-col cols="12" md="6"><v-text-field :label="t('employeeDetails.balanceDialog.annual')" type="number" v-model="balanceForm.annual_leave_days" outlined></v-text-field></v-col>
                    <v-col cols="12" md="6"><v-text-field :label="t('employeeDetails.balanceDialog.carryover')" type="number" v-model="balanceForm.carryover_days" outlined></v-text-field></v-col>
                    <v-col cols="12" md="6"><v-text-field :label="t('employeeDetails.balanceDialog.accrualStart')" type="date" v-model="balanceForm.accrual_start_date" outlined></v-text-field></v-col>
                    <v-col cols="12" md="6"><v-text-field :label="t('employeeDetails.balanceDialog.accrualFinish')" type="date" v-model="balanceForm.accrual_finish_date" outlined></v-text-field></v-col>
                    <v-col cols="12" md="6"><v-text-field :label="t('employeeDetails.balanceDialog.totalAccrued')" type="number" v-model="balanceForm.total_accrued_days" outlined readonly></v-text-field></v-col>
                    <v-col cols="12" md="6"><v-text-field :label="t('employeeDetails.balanceDialog.totalTaken')" type="number" v-model="balanceForm.total_taken_days" outlined></v-text-field></v-col>
                    <v-col cols="12" md="6"><v-text-field :label="t('employeeDetails.balanceDialog.remaining')" type="number" v-model="balanceForm.remaining_days" outlined></v-text-field></v-col>
                    <v-col cols="12"><v-textarea :label="t('employeeDetails.balanceDialog.notes')" v-model="balanceForm.notes" rows="2" outlined></v-textarea></v-col>
                  </v-row>
                </v-container>
              </v-form>
            </v-card-text>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn variant="text" @click="balanceDialog = false">{{ t("app.actions.cancel") }}</v-btn>
              <v-btn color="primary" :loading="balanceDialogLoading" @click="submitBalance">
                {{ t("employeeDetails.balanceDialog.save") }}
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>

        <v-snackbar
          v-model="approvalSnackbar.visible"
          :color="approvalSnackbar.color"
          timeout="4000"
          location="bottom"
        >
          {{ approvalSnackbar.message }}
          <template #actions>
            <v-btn variant="text" @click="approvalSnackbar.visible = false">{{ t("app.actions.cancel") }}</v-btn>
          </template>
        </v-snackbar>
        <v-dialog v-model="approvalDialog.visible" max-width="420">
          <v-card>
            <v-card-title class="text-h6">
              {{ t(approvalDialog.status === 'approved' ? "employeeDetails.approvals.dialogApproved" : "employeeDetails.approvals.dialogRejected") }}
            </v-card-title>
            <v-card-text class="text-body-2">
              <p class="mb-2">
                {{ t(approvalDialog.status === 'approved' ? "employeeDetails.approvals.dialogApprovedYou" : "employeeDetails.approvals.dialogRejectedYou") }}
                <strong>{{ approvalDialog.employeeName }}</strong>.
              </p>
              <p class="mb-1">
                <strong>{{ t("employeeDetails.approvals.period") }}:</strong> {{ approvalDialog.period || '—' }}
              </p>
              <p class="mb-1">
                <strong>{{ t("employeeDetails.approvals.days") }}:</strong> {{ formatNumber(approvalDialog.days) }}
              </p>
              <p>
                <strong>{{ t("employeeDetails.approvals.holidayType") }}:</strong> {{ approvalDialog.holidayType || t("employeeDetails.info.unknownHolidayType") }}
              </p>
              <p v-if="approvalDialog.notes" class="mt-2">
                <strong>{{ t("employeeDetails.approvals.reason") }}:</strong> {{ approvalDialog.notes }}
              </p>
            </v-card-text>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn color="primary" variant="text" @click="approvalDialog.visible = false">
                {{ t("app.actions.cancel") }}
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
      </v-container>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import { defineComponent, ref, onMounted, computed, watch, reactive } from "vue";
import { useI18n } from "vue-i18n";
import { computeWorkingDays as computeWorkingDaysUtil, normalizeWorkingDate } from "@/Utile/computeWorkingDays.mjs";
import {
  toNumber,
  formatNumber,
  formatDate,
  formatDateTime,
  formatAsIsoDate,
  normalizeDateString,
  toDateObject,
  computeAccruedUntilYearEnd,
} from "@/Utile/holidayFormatting.mjs";
import { useRoute, useRouter } from "vue-router";
import HolidayRequestPopUp from "./holiday/HolidayRequestPopUp.vue";
import {
  deriveEmployeeId as deriveEmployeeIdHelper,
  parseEmployeeId as parseEmployeeIdHelper,
  parseCompanyId,
} from "@/components/dashboard/utils.mjs";

const APPROVER_ROLES = ["admin", "miniadmin", "vacation-manager", "vacation-director", "vacation-hr"];

export default defineComponent({
  name: "EmployeeDetailsExt",
  components: {
    HolidayRequestPopUp,
  },
  props: {
    employeeId: {
      type: [Number, String],
      default: null,
    },
    allowHolidayRequests: {
      type: Boolean,
      default: true,
    },
    showBalanceUpdate: {
      type: Boolean,
      default: true,
    },
    fillViewport: {
      type: Boolean,
      default: true,
    },
    showTopBalanceCard: {
      type: Boolean,
      default: true,
    },
    showFluxSummary: {
      type: Boolean,
      default: true,
    },
  },
  setup(props) {
    const { t } = useI18n();
    const route = useRoute();
    const router = useRouter();

    const parseEmployeeId = (value) => parseEmployeeIdHelper(value);

    const allowHolidayRequests = computed(() => props.allowHolidayRequests !== false);
    const showBalanceUpdate = computed(() => props.showBalanceUpdate !== false);
    const fillViewport = computed(() => props.fillViewport !== false);
    const showTopBalanceCard = computed(() => props.showTopBalanceCard !== false);
    const showFluxSummary = computed(() => props.showFluxSummary !== false);
    const mainClassObject = computed(() => ({
      "employee-main": true,
      "employee-main--compact": !fillViewport.value,
    }));

    const employeeId = ref(
      deriveEmployeeIdHelper({
        propId: props.employeeId,
        routeParamId: route.params.id,
        routeQueryId: route.query.id,
        storageId: localStorage.getItem("user-employee-id"),
      })
    );
    const employee = ref({});
    const employeesList = ref([]);
    const companiesList = ref([]);
    const holidayTypes = ref([]);
    const holidayBalance = ref(null);
    const holidayRequests = ref([]);
    const nonWorkingDays = ref([]);
    const loadingRequests = ref(false);
    const activeVacationTab = ref("requests");
    const approvalRequests = ref([]);
    const loadingApprovals = ref(false);
    const approvalsError = ref(null);
    const approvalActionState = reactive({ targetId: null, decision: null, loading: false });
    const approvalSnackbar = reactive({ visible: false, message: "", color: "success" });
    const approvalDialog = reactive({
      visible: false,
      status: "approved",
      employeeName: "",
      period: "",
      days: 0,
      holidayType: "",
      notes: "",
    });
    const isProcessingApprovalToken = ref(false);
    const loggedInEmployeeId = ref(parseEmployeeId(localStorage.getItem("user-employee-id")));

    const resetEmployeeState = () => {
      employee.value = {};
      holidayBalance.value = null;
      holidayRequests.value = [];
      nonWorkingDays.value = [];
      activeVacationTab.value = "requests";
      loadingRequests.value = false;
      approvalRequests.value = [];
      approvalsError.value = null;
      loadingApprovals.value = false;
      approvalActionState.targetId = null;
      approvalActionState.decision = null;
      approvalActionState.loading = false;
      approvalSnackbar.visible = false;
      isProcessingApprovalToken.value = false;
      approvalDialog.visible = false;
    };

    const requestDialog = ref(false);
    const requestDialogLoading = ref(false);
    const requestForm = ref({
      holiday_type_id: null,
      start_date: "",
      end_date: "",
      working_days_requested: 1,
      status: "pending",
      approved: false,
      notes: "",
    });
    const requestEditingId = ref(null);
    const requestRangeMenu = ref(false);
    const requestRangeActiveEdge = ref("start");

    const balanceDialog = ref(false);
    const balanceDialogLoading = ref(false);
    const balanceDialogMode = ref("update");
    const balanceForm = ref({
      annual_leave_days: 0,
      accrual_start_date: "",
      accrual_finish_date: "",
      carryover_days: 0,
      total_accrued_days: 0,
      total_taken_days: 0,
      remaining_days: 0,
      notes: "",
    });

    const showAllRequests = ref(false);

    const userRole = ref(localStorage.getItem("user-role") || "");
    const normalizedRole = computed(() => (userRole.value || "").toLowerCase());
    const canViewApprovals = computed(() =>
      ["manager", "director", "hr", "admin", "miniadmin"].includes(normalizedRole.value)
    );
    const canSeeChecklistNotifications = computed(() =>
      ["user", "manager", "director"].includes(normalizedRole.value)
    );
    const taskNotifications = ref({
      total: 0,
      overdue: 0,
      loading: false,
      error: null,
    });
    const showTaskNotifications = computed(
      () =>
        canSeeChecklistNotifications.value &&
        !taskNotifications.value.loading &&
        (taskNotifications.value.total > 0 || taskNotifications.value.overdue > 0)
    );
    const isSelfProfile = computed(() => {
      const currentId = parseEmployeeId(employee.value?.id);
      const viewerId = loggedInEmployeeId.value;
      if (!currentId || !viewerId) return false;
      return currentId === viewerId;
    });
    const showApprovalsSection = computed(() => canViewApprovals.value && isSelfProfile.value);
    const filteredApprovalRequests = computed(() => {
      const managerId = loggedInEmployeeId.value;
      if (!managerId) return [];
      return approvalRequests.value.filter((item) => Number(item?.employee_manager_id) === Number(managerId));
    });
    const showApprovalsCard = computed(
      () =>
        showApprovalsSection.value &&
        (loadingApprovals.value ||
          filteredApprovalRequests.value.length > 0 ||
          isProcessingApprovalToken.value ||
          Boolean(approvalsError.value))
    );

    const isCancellationRequest = (approval) =>
      String(approval?.pending_action || "").toLowerCase() === "cancel";

    const statusMapping = computed(() => ({
      pending: { label: t("employeeDetails.statuses.pending"), color: "amber" },
      approved: { label: t("employeeDetails.statuses.approved"), color: "green" },
      rejected: { label: t("employeeDetails.statuses.rejected"), color: "error" },
      cancelled: { label: t("employeeDetails.statuses.cancelled"), color: "grey" },
      draft: { label: t("employeeDetails.statuses.draft"), color: "blue-grey" },
      cancelPending: { label: t("employeeDetails.statuses.cancelPending"), color: "error" },
    }));

    const approvalChipLabel = (approval) =>
      isCancellationRequest(approval)
        ? t("employeeDetails.approvals.chipCancel")
        : approval?.holiday_type_name || t("employeeDetails.info.unknownHolidayType");

    const approvalChipColor = (approval) =>
      isCancellationRequest(approval) ? "error" : "amber";

    const approvalInfoText = (approval) =>
      isCancellationRequest(approval)
        ? t("employeeDetails.approvals.cancelInfo")
        : null;

    const approveButtonLabel = (approval) =>
      isCancellationRequest(approval)
        ? t("employeeDetails.approvals.confirmCancel")
        : t("employeeDetails.approvals.approve");

    const rejectButtonLabel = (approval) =>
      isCancellationRequest(approval)
        ? t("employeeDetails.approvals.keepLeave")
        : t("employeeDetails.approvals.reject");

    const showApprovalMessage = (message, color = "success", details = null) => {
      if (color === "success" && details) {
        approvalDialog.status = details.status || "approved";
        approvalDialog.employeeName = details.employee_name || "";
        const start = details.start_date ? formatDate(details.start_date) : null;
        const end = details.end_date ? formatDate(details.end_date) : null;
        approvalDialog.period = start && end ? `${start} – ${end}` : start || end || "";
        approvalDialog.days = details.working_days_requested ?? 0;
        approvalDialog.holidayType =
          details.holiday_type_name || lookupHolidayType(details.holiday_type_id) || t("employeeDetails.info.unknownHolidayType");
        approvalDialog.notes = details.notes || "";
        approvalDialog.visible = true;
        return;
      }

      approvalSnackbar.message = message;
      approvalSnackbar.color = color;
      approvalSnackbar.visible = true;
    };

    const getPreferredCompanyId = () => {
      const companyFromEmployee = parseCompanyId(employee.value?.company_id);
      if (companyFromEmployee) return companyFromEmployee;
      return parseCompanyId(localStorage.getItem("user-company-id"));
    };

    const fetchChecklistNotifications = async () => {
      if (!canSeeChecklistNotifications.value) {
        taskNotifications.value = { total: 0, overdue: 0, loading: false, error: null };
        return;
      }
      const companyId = getPreferredCompanyId();
      const empId = parseEmployeeId(employee.value?.id);
      if (!companyId || !empId) {
        taskNotifications.value = { total: 0, overdue: 0, loading: false, error: null };
        return;
      }
      taskNotifications.value.loading = true;
      taskNotifications.value.error = null;
      try {
        const params = { companyId, employeeId: empId, includeClosed: false };
        const { data } = await axios.get("/checklist/tasks", { params });
        const items = data?.items || [];
        const now = new Date();
        const overdue = items.filter((t) => t.due_date && new Date(t.due_date) < now).length;
        taskNotifications.value = {
          total: items.length,
          overdue,
          loading: false,
          error: null,
        };
      } catch (error) {
        console.error("[notifications] checklist summary", error);
        taskNotifications.value = { total: 0, overdue: 0, loading: false, error: error?.message || "Eroare" };
      }
    };

    const approvalDecisionFromQuery = (value) => {
      const normalized = String(value || "").toLowerCase();
      if (["approve", "approved", "accept", "yes", "true"].includes(normalized)) {
        return "approve";
      }
      if (["reject", "rejected", "deny", "decline", "no", "false"].includes(normalized)) {
        return "reject";
      }
      return "approve";
    };

    async function fetchApprovals() {
      if (!showApprovalsSection.value) {
        approvalRequests.value = [];
        return;
      }

      loadingApprovals.value = true;
      approvalsError.value = null;
      try {
        const params = {};
        const companyId = getPreferredCompanyId();
        if (companyId) {
          params.company_id = companyId;
        }
        const { data } = await axios.get("/holidays/approvals", { params });
        approvalRequests.value = Array.isArray(data) ? data : [];
      } catch (error) {
        approvalsError.value =
          error?.response?.data?.message || t("employeeDetails.approvals.loadError");
        console.error("Eroare fetchApprovals:", error);
      } finally {
        loadingApprovals.value = false;
      }
    }

    const approvalActionLoading = (requestId) =>
      approvalActionState.loading && approvalActionState.targetId === requestId;

    async function handleApprovalAction(request, decision) {
      if (!request?.id || !["approved", "rejected"].includes(decision)) return;
      if (!showApprovalsSection.value) {
        showApprovalMessage(t("employeeDetails.approvals.noPermission"), "error");
        return;
      }

      approvalActionState.targetId = request.id;
      approvalActionState.decision = decision;
      approvalActionState.loading = true;
      try {
        const payload = {};
        const approverId = loggedInEmployeeId.value || employeeId.value;
        if (decision === "approved" && approverId) {
          payload.approved_by_employee_id = approverId;
        }
        if (request.notes) {
          payload.notes = request.notes;
        }
        const endpoint = decision === "approved" ? "approve" : "reject";
        const { data } = await axios.post(`/holidays/approvals/${request.id}/${endpoint}`, payload);
        const responseDetails = data?.request || {
          ...request,
          status: data?.status || decision,
          employee_name:
            request.employee_name || request.employee_full_name || `${request.employee_first_name || ''} ${request.employee_last_name || ''}`.trim(),
          notes: request.notes || "",
        };
        showApprovalMessage(
          data?.message ||
            (decision === "approved"
              ? t("employeeDetails.approvals.successApprove")
              : t("employeeDetails.approvals.successReject")),
          "success",
          responseDetails
        );
        await fetchApprovals();
        await fetchHolidayData();
      } catch (error) {
        const message =
          error?.response?.data?.message || t("employeeDetails.approvals.updateError");
        showApprovalMessage(message, "error");
        console.error("Eroare handleApprovalAction:", error);
      } finally {
        approvalActionState.targetId = null;
        approvalActionState.decision = null;
        approvalActionState.loading = false;
      }
    }

    async function processApprovalTokenFromQuery({ refreshAfterSuccess = true } = {}) {
      const token = route.query?.approvalToken;
      if (!token || !showApprovalsSection.value) return false;
      if (isProcessingApprovalToken.value) return false;
      isProcessingApprovalToken.value = true;

      const decision = approvalDecisionFromQuery(
        route.query?.decision || route.query?.tokenDecision || "approve"
      );
      const approverId = loggedInEmployeeId.value || employeeId.value;
      const payload = { decision };
      if (decision === "approve" && approverId) {
        payload.approved_by_employee_id = approverId;
      }
      if (route.query?.tokenNotes) {
        payload.notes = route.query.tokenNotes;
      }

      try {
        const { data } = await axios.post(`/holidays/approvals/token/${token}`, payload);
        const resultStatus = data?.status || (decision === "approve" ? "approved" : decision);
        const details = data?.request
          ? { ...data.request, status: resultStatus }
          : null;
        showApprovalMessage(
          data?.message ||
            (decision === "approve"
              ? t("employeeDetails.approvals.successApprove")
              : t("employeeDetails.approvals.successReject")),
          "success",
          details
        );
        if (refreshAfterSuccess) {
          await fetchApprovals();
          await fetchHolidayData();
        }
      } catch (error) {
        const message =
          error?.response?.data?.message || t("employeeDetails.approvals.tokenError");
        showApprovalMessage(message, "error");
        console.error("Eroare processApprovalTokenFromQuery:", error);
      } finally {
        isProcessingApprovalToken.value = false;
        const nextQuery = { ...route.query };
        delete nextQuery.approvalToken;
        delete nextQuery.decision;
        delete nextQuery.tokenDecision;
        delete nextQuery.tokenNotes;
        delete nextQuery.requestId;
        router.replace({ query: nextQuery }).catch(() => {});
      }

      return true;
    }

    const truncate = (value, length = 40) => {
      if (!value || typeof value !== "string") return value;
      return value.length > length ? `${value.slice(0, length)}…` : value;
    };

    const requestHeaders = computed(() => [
      { key: "holiday_type_name", title: t("employeeDetails.tables.requests.type") },
      { key: "period", title: t("employeeDetails.tables.requests.period") },
      { key: "working_days_requested", title: t("employeeDetails.tables.requests.days") },
      { key: "status", title: t("employeeDetails.tables.requests.status") },
      { key: "approved_by_name", title: t("employeeDetails.tables.requests.approvedBy") },
      { key: "approved_at", title: t("employeeDetails.tables.requests.approvedAt") },
      { key: "actions", title: t("employeeDetails.tables.requests.actions"), sortable: false },
    ]);

   const historyHeaders = computed(() => [
     { key: "holiday_type_name", title: t("employeeDetails.tables.history.type") },
     { key: "start_date", title: t("employeeDetails.tables.history.start") },
     { key: "end_date", title: t("employeeDetails.tables.history.end") },
     { key: "working_days_requested", title: t("employeeDetails.tables.history.days") },
     { key: "status", title: t("employeeDetails.tables.history.status") },
     { key: "created_at", title: t("employeeDetails.tables.history.created") },
   ]);

    const statusOptions = computed(() =>
      Object.entries(statusMapping.value)
        .filter(([key]) => key !== "cancelPending")
        .map(([value, meta]) => ({ value, title: meta.label }))
    );

    const initials = computed(() => {
      const fn = employee.value.first_name?.[0] || "";
      const ln = employee.value.last_name?.[0] || "";
      return `${fn}${ln}`.toUpperCase() || "?";
    });

    const fullName = computed(() => `${employee.value.first_name || ''} ${employee.value.last_name || ''}`.trim());

    const personalInfo = computed(() => [
      {
        label: t("employeeDetails.personal.cnp"),
        value: employee.value.cnp || "—",
        icon: "mdi-card-account-details-outline",
      },
      {
        label: t("employeeDetails.personal.birthDate"),
        value: formatDate(employee.value.birth_date) || "—",
        icon: "mdi-cake-variant",
      },
      {
        label: t("employeeDetails.personal.gender"),
        value: employee.value.gender || "—",
        icon: "mdi-gender-male-female",
      },
      {
        label: t("employeeDetails.personal.nationality"),
        value: employee.value.nationality || "—",
        icon: "mdi-earth",
      },
      {
        label: t("employeeDetails.personal.maritalStatus"),
        value: employee.value.marital_status || "—",
        icon: "mdi-ring",
      },
      {
        label: t("employeeDetails.personal.phone"),
        value: employee.value.phone || "—",
        icon: "mdi-phone",
        clickable: !!employee.value.phone,
        click: () => handlePhoneClick(employee.value.phone),
      },
      {
        label: t("employeeDetails.personal.email"),
        value: employee.value.email || "—",
        icon: "mdi-email",
        clickable: !!employee.value.email,
        click: () => handleEmailClick(employee.value.email),
      },
      {
        label: t("employeeDetails.personal.address"),
        value: employee.value.address || "—",
        icon: "mdi-map-marker",
      },
    ]);

    const professionalInfo = computed(() => [
      {
        label: t("employeeDetails.professional.company"),
        value: employee.value.company_name || "—",
        icon: "mdi-office-building",
      },
      {
        label: t("employeeDetails.professional.department"),
        value: employee.value.department || "—",
        icon: "mdi-account-group-outline",
      },
      {
        label: t("employeeDetails.professional.contractType"),
        value: employee.value.type_of_contract || "—",
        icon: "mdi-briefcase-outline",
      },
      {
        label: t("employeeDetails.professional.salary"),
        value: employee.value.salary ? `${employee.value.salary}` : "—",
        icon: "mdi-currency-usd",
      },
      {
        label: t("employeeDetails.professional.hireDate"),
        value: formatDate(employee.value.hire_date) || "—",
        icon: "mdi-calendar-start",
      },
    ]);

    const holidayTypeOptions = computed(() =>
      holidayTypes.value.map((type) => ({
        title: `${type.name_ro}${Number(type.is_paid) ? ' • plătit' : ''}`,
        value: type.id,
      }))
    );

    const ANNUAL_LEAVE_CODES = new Set(["annual_leave"]);
    const ANNUAL_LEAVE_NAMES = new Set([
      "Concediu anual de odihnă",
      "Concediu anual de odihnă - plătit",
      "Concediu anual de odihnă • plătit",
    ]);

    const holidayTypeMap = computed(() => {
      const map = new Map();
      holidayTypes.value.forEach((type) => {
        map.set(Number(type.id), type);
      });
      return map;
    });

    const isAnnualLeaveType = (type) => {
      if (!type) return false;
      if (type.code && ANNUAL_LEAVE_CODES.has(type.code)) return true;
      if (type.name_ro && ANNUAL_LEAVE_NAMES.has(type.name_ro)) return true;
      return false;
    };

    const currentHolidayType = computed(() =>
      holidayTypeMap.value.get(Number(requestForm.value.holiday_type_id)) || null
    );

    const currentTypeReducesBalance = computed(() => isAnnualLeaveType(currentHolidayType.value));

    const visibleHolidayRequests = computed(() =>
      showAllRequests.value ? holidayRequests.value : holidayRequests.value.slice(0, 2)
    );

    const goToMyHolidays = () => {
      const id = employeeId.value;
      if (!id) return;
      if (route?.name === 'ExternalDashboard') {
        window.dispatchEvent(
          new CustomEvent('ext-dashboard:navigate', { detail: { optionId: 'my-holidays' } })
        );
      } else {
        router.push({ name: 'EmployeeHolidays', query: { id: String(id) } });
      }
    };

    const subordinateEmployees = computed(() =>
      employeesList.value.filter((emp) => Number(emp.id_manager) === employeeId.value)
    );

    const subordinatePendingRequests = computed(() =>
      holidayRequests.value.filter(
        (req) => req.status === "pending" && subordinateEmployees.value.some((emp) => emp.id === req.employee_id)
      )
    );

    const statistics = computed(() => {
      const totalAccrued = displayBalance.value.totalAccrued;
      const totalTaken = totalTakenDisplay.value;
      const remaining = Number(holidayBalance.value?.remaining_days || 0);
      const currentYear = new Date().getFullYear();
      const approvedDaysYTD = holidayRequests.value
        .filter((req) => req.status === "approved" && new Date(req.start_date).getFullYear() === currentYear)
        .reduce((sum, req) => sum + getWorkingDays(req), 0);

      const byTypeMap = new Map();
      holidayRequests.value
        .filter((req) => req.status === "approved")
        .forEach((req) => {
          const key = req.holiday_type_id;
          const existing = byTypeMap.get(key) || { days: 0, label: lookupHolidayType(key) || 'Tip necunoscut' };
          existing.days += getWorkingDays(req);
          byTypeMap.set(key, existing);
        });

      const byType = Array.from(byTypeMap.entries()).map(([type, data]) => ({
        type,
        label: data.label,
        days: data.days,
        percent: totalTaken ? (data.days / totalTaken) * 100 : 0,
      }));

      return {
        totalAccrued,
        remainingDays: remaining,
        approvedDaysYTD,
        usagePercent: totalAccrued ? (totalTaken / totalAccrued) * 100 : 0,
        byType,
      };
    });

    const canManageRequests = computed(() => APPROVER_ROLES.includes(normalizedRole.value));

    function lookupHolidayType(id) {
      const type = holidayTypes.value.find((t) => Number(t.id) === Number(id));
      return type ? type.name_ro : null;
    }

    function statusLabel(status) {
      return statusMapping.value[status]?.label || status;
    }

    function statusChipColor(status) {
      return statusMapping.value[status]?.color || "grey";
    }

    const nonWorkingSet = computed(() => {
      const set = new Set();
      nonWorkingDays.value.forEach((entry) => {
        const iso = normalizeDateString(entry?.occurrence_date || entry?.date);
        if (iso) {
          set.add(iso);
        }
      });
      return set;
    });

    const handleRangePointerDown = (edge) => {
      requestRangeActiveEdge.value = edge;
    };

    const handleRangeFieldFocus = (edge) => {
      requestRangeActiveEdge.value = edge;
    };

    const handleRangeKeyboardOpen = (edge) => {
      requestRangeActiveEdge.value = edge;
      requestRangeMenu.value = true;
    };

    const handleRangeControlClick = (edge, event, menuProps) => {
      if (event && typeof event.preventDefault === "function") {
        event.preventDefault();
      }
      if (event && typeof event.stopPropagation === "function") {
        event.stopPropagation();
      }
      requestRangeActiveEdge.value = edge;
      requestRangeMenu.value = true;
      if (menuProps && typeof menuProps.onClick === "function") {
        menuProps.onClick(event);
      }
    };

    const recalculateTotalLeaveDays = () => {
      const total = computeAccruedUntilYearEnd(
        balanceForm.value.annual_leave_days,
        balanceForm.value.carryover_days,
        balanceForm.value.accrual_start_date,
        balanceForm.value.accrual_finish_date || holidayBalance.value?.accrual_finish_date || null
      );
      const currentTaken = toNumber(balanceForm.value.total_taken_days, 0);
      balanceForm.value.total_accrued_days = total;
      balanceForm.value.remaining_days = Number((total - currentTaken).toFixed(2));
    };

    const computeWorkingDays = (start, end) => {
      return computeWorkingDaysUtil(start, end, {
        nonWorkingDates: nonWorkingSet.value,
      });
    };

    function getWorkingDays(request) {
      if (!request) return 0;
      const recorded = toNumber(request.working_days_requested, 0);
      if (recorded) return recorded;
      const start = request.start_date ? formatDate(request.start_date) || request.start_date : "";
      const end = request.end_date ? formatDate(request.end_date) || request.end_date : "";
      return computeWorkingDays(start, end);
    }

    const hasApprovedOverlap = (startValue, endValue, excludeId = null) => {
      if (!startValue || !endValue) return false;
      if (!currentTypeReducesBalance.value) return false;
      const startDate = new Date(startValue);
      const endDate = new Date(endValue);
      if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) return false;
      if (endDate < startDate) return false;

      return holidayRequests.value.some((req) => {
        if (excludeId && Number(req.id) === Number(excludeId)) return false;
        if (req.status !== "approved") return false;
        const type = holidayTypeMap.value.get(Number(req.holiday_type_id));
        if (!isAnnualLeaveType(type)) return false;
        const reqStart = new Date(req.start_date);
        const reqEnd = new Date(req.end_date);
        if (Number.isNaN(reqStart.getTime()) || Number.isNaN(reqEnd.getTime())) return false;
        return startDate <= reqEnd && endDate >= reqStart;
      });
    };

    const totalTakenDisplay = computed(() => {
      const fromBalance = toNumber(holidayBalance.value?.total_taken_days, 0);
      if (fromBalance > 0) return fromBalance;
      return holidayRequests.value
        .filter((req) => req.status === "approved")
        .filter((req) => isAnnualLeaveType(holidayTypeMap.value.get(Number(req.holiday_type_id))))
        .reduce((sum, req) => sum + getWorkingDays(req), 0);
    });

    const displayBalance = computed(() => {
      const annual = toNumber(holidayBalance.value?.annual_leave_days, 0);
      const carry = toNumber(holidayBalance.value?.carryover_days, 0);
      const accrualStartRaw = holidayBalance.value?.accrual_start_date;
      const totalAccrued = computeAccruedUntilYearEnd(
        annual,
        carry,
        accrualStartRaw,
        holidayBalance.value?.accrual_finish_date || null
      );
      const totalTaken = totalTakenDisplay.value;
      const remaining = Number((totalAccrued - totalTaken).toFixed(2));
      return {
        annual,
        totalAccrued,
        totalTaken,
        remaining,
      };
    });

    const computeDaySetForStatus = (status) => {
      const set = new Set();
      const editingId = requestEditingId.value ? Number(requestEditingId.value) : null;
      holidayRequests.value.forEach((req) => {
        if (req.status !== status) return;
        if (editingId && Number(req.id) === editingId) return;
        const type = holidayTypeMap.value.get(Number(req.holiday_type_id));
        if (!isAnnualLeaveType(type)) return;
        const start = toDateObject(req.start_date);
        const end = toDateObject(req.end_date);
        if (!start || !end) return;
        const cursor = new Date(start);
        while (cursor <= end) {
          const iso = formatAsIsoDate(cursor);
          if (iso) {
            set.add(iso);
          }
          cursor.setDate(cursor.getDate() + 1);
        }
      });
      return set;
    };

    const blockedDaysSet = computed(() => computeDaySetForStatus("approved"));
    const pendingDaysSet = computed(() => computeDaySetForStatus("pending"));

    const calendarEvents = computed(() => {
      if (!currentTypeReducesBalance.value) return [];
      const map = new Map();
      nonWorkingSet.value.forEach((iso) => {
        const date = toDateObject(iso);
        if (date) {
          map.set(iso, { date, color: "neutral" });
        }
      });
      pendingDaysSet.value.forEach((iso) => {
        const date = toDateObject(iso);
        if (date) {
          map.set(iso, { date, color: "warning" });
        }
      });
      blockedDaysSet.value.forEach((iso) => {
        const date = toDateObject(iso);
        if (date) {
          map.set(iso, { date, color: "error" });
        }
      });
      return Array.from(map.values());
    });

    const allowedCalendarDates = (value) => {
      const iso = normalizeDateString(value);
      if (!iso) return false;
      const finish = normalizeDateString(holidayBalance.value?.accrual_finish_date);
      if (finish && iso > finish) return false;
      if (!currentTypeReducesBalance.value) return true;
      if (nonWorkingSet.value.has(iso)) {
        if (requestEditingId.value) {
          const editing = holidayRequests.value.find((req) => Number(req.id) === Number(requestEditingId.value));
          if (editing) {
            const startIso = normalizeDateString(editing.start_date);
            const endIso = normalizeDateString(editing.end_date);
            if (startIso && endIso && startIso <= iso && iso <= endIso) {
              // permite păstrarea selecției existente, dar nu noi intervale.
              return true;
            }
          }
        }
        return false;
      }
      if (pendingDaysSet.value.has(iso)) {
        if (requestEditingId.value) {
          const editing = holidayRequests.value.find((req) => Number(req.id) === Number(requestEditingId.value));
          if (editing && editing.status === "pending") {
            const startIso = normalizeDateString(editing.start_date);
            const endIso = normalizeDateString(editing.end_date);
            if (startIso && endIso && startIso <= iso && iso <= endIso) {
              return true;
            }
          }
        }
        return false;
      }
      return !blockedDaysSet.value.has(iso);
    };

    const rangeDateModel = computed(() => {
      const start = toDateObject(requestForm.value.start_date);
      const end = toDateObject(requestForm.value.end_date);

      if (start && end) {
        if (start <= end) {
          return [start, end];
        }
        return [end, start];
      }

      if (start) {
        return [start];
      }

      return [];
    });

    const normalizeRangeSelection = (value) => {
      if (!value) {
        return { start: "", end: "", hasStart: false, hasEnd: false };
      }

      let startValue = null;
      let endValue = null;

      if (Array.isArray(value)) {
        [startValue, endValue] = value;
      } else if (value instanceof Date || typeof value === "string") {
        startValue = value;
      } else if (typeof value === "object") {
        if ("start" in value || "end" in value) {
          startValue = value.start ?? null;
          endValue = value.end ?? null;
        }
      }

      const startIso = normalizeDateString(startValue);
      const endIso = normalizeDateString(endValue);

      if (startIso && endIso) {
        if (startIso <= endIso) {
          return { start: startIso, end: endIso, hasStart: true, hasEnd: true };
        }
        return { start: endIso, end: startIso, hasStart: true, hasEnd: true };
      }

      return {
        start: startIso || "",
        end: endIso || "",
        hasStart: Boolean(startIso),
        hasEnd: Boolean(endIso),
      };
    };

    const onCalendarRangeChange = (value) => {
      const selection = normalizeRangeSelection(value);
      const targetEdge = requestRangeActiveEdge.value || "start";
      const currentStart = requestForm.value.start_date;
      const currentEnd = requestForm.value.end_date;

      if (targetEdge === "end") {
        const candidate = selection.hasEnd
          ? selection.end
          : selection.hasStart
          ? selection.start
          : "";

        if (candidate) {
          if (currentStart && currentStart > candidate) {
            requestForm.value.start_date = candidate;
            requestForm.value.end_date = currentStart;
          } else {
            requestForm.value.end_date = candidate;
            if (!currentStart && selection.hasStart) {
              requestForm.value.start_date = selection.start;
            }
          }

          if (requestForm.value.start_date && requestForm.value.end_date) {
            requestRangeMenu.value = false;
          }
        }

        applyWorkingDays();
        return;
      }

      const candidateStart = selection.hasStart
        ? selection.start
        : selection.hasEnd
        ? selection.end
        : "";

      if (candidateStart) {
        if (selection.hasStart && selection.hasEnd) {
          const nextStart = selection.start;
          const nextEnd = selection.end;
          if (nextStart <= nextEnd) {
            requestForm.value.start_date = nextStart;
            requestForm.value.end_date = nextEnd;
          } else {
            requestForm.value.start_date = nextEnd;
            requestForm.value.end_date = nextStart;
          }
          requestRangeMenu.value = false;
        } else {
          requestForm.value.start_date = candidateStart;
          if (currentEnd && currentEnd < candidateStart) {
            requestForm.value.end_date = candidateStart;
          }
          if (!selection.hasEnd) {
            requestRangeActiveEdge.value = "end";
          }
        }
      }

      applyWorkingDays();
    };


    function applyWorkingDays() {
      requestForm.value.working_days_requested = computeWorkingDays(
        requestForm.value.start_date,
        requestForm.value.end_date
      );
    }

    function openRequestDialog(request = null) {
      if (!allowHolidayRequests.value) return;
      if (request) {
        requestEditingId.value = request.id;
        requestForm.value = {
          holiday_type_id: request.holiday_type_id,
          start_date: formatDate(request.start_date),
          end_date: formatDate(request.end_date),
          working_days_requested: Number(request.working_days_requested || 1),
          status: request.status,
          approved: Number(request.approved) === 1,
          notes: request.notes || "",
        };
      } else {
        requestEditingId.value = null;
        let today = new Date();
        let formatted = formatAsIsoDate(today);
        const finish = normalizeDateString(holidayBalance.value?.accrual_finish_date);
        if (finish) {
          const finishDate = toDateObject(finish);
          if (!finishDate || finishDate < today) {
            alert(t("employeeHolidays.errors.afterFinish", { date: finish }));
            return;
          }
        }
        while (hasApprovedOverlap(formatted, formatted)) {
          today.setDate(today.getDate() + 1);
          formatted = formatAsIsoDate(today);
        }
        requestForm.value = {
          holiday_type_id: holidayTypeOptions.value[0]?.value || null,
          start_date: formatted,
          end_date: formatted,
          working_days_requested: 1,
          status: "pending",
          approved: false,
          notes: "",
        };
      }
      applyWorkingDays();
      requestRangeActiveEdge.value = "start";
      requestDialog.value = true;
    }

    watch(
      () => requestForm.value.approved,
      (approved) => {
        if (approved && requestForm.value.status !== "approved") {
          requestForm.value.status = "approved";
        }
      }
    );

    watch(
      () => requestForm.value.status,
      (status) => {
        if (status === "approved" && !requestForm.value.approved) {
          requestForm.value.approved = true;
        } else if (status !== "approved" && requestForm.value.approved) {
          requestForm.value.approved = false;
        }
      }
    );

    watch(
      () => [
        balanceForm.value.annual_leave_days,
        balanceForm.value.carryover_days,
        balanceForm.value.accrual_start_date,
        balanceForm.value.accrual_finish_date,
        balanceForm.value.total_taken_days,
      ],
      () => {
        recalculateTotalLeaveDays();
      }
    );

    watch(balanceDialog, (isOpen) => {
      if (!isOpen) {
        balanceDialogMode.value = "update";
      }
    });

    function openBalanceDialog(mode = "update") {
      balanceDialogMode.value = mode;
      balanceForm.value = {
        annual_leave_days: holidayBalance.value?.annual_leave_days || 0,
        accrual_start_date: formatDate(holidayBalance.value?.accrual_start_date) || formatAsIsoDate(new Date()),
        accrual_finish_date: formatDate(holidayBalance.value?.accrual_finish_date) || "",
        carryover_days: holidayBalance.value?.carryover_days || 0,
        total_accrued_days: holidayBalance.value?.total_accrued_days || 0,
        total_taken_days:
          toNumber(holidayBalance.value?.total_taken_days, 0) || totalTakenDisplay.value,
        remaining_days: holidayBalance.value?.remaining_days || 0,
        notes: holidayBalance.value?.notes || "",
      };
      recalculateTotalLeaveDays();
      balanceDialog.value = true;
    }

    async function submitHolidayRequest() {
      if (!allowHolidayRequests.value) return;
      if (!employee.value.company_id) {
        alert(t("employeeHolidays.errors.unknownCompany"));
        return;
      }
      const start = requestForm.value.start_date;
      const end = requestForm.value.end_date;
      const finish = normalizeDateString(holidayBalance.value?.accrual_finish_date);
      if (finish && end && end > finish) {
        alert(t("employeeHolidays.errors.afterFinish", { date: finish }));
        return;
      }
      if (hasApprovedOverlap(start, end, requestEditingId.value)) {
        alert(t("employeeHolidays.errors.overlap"));
        return;
      }
      requestDialogLoading.value = true;
      applyWorkingDays();
      const payload = {
        company_id: employee.value.company_id,
        employee_id: employeeId.value,
      holiday_type_id: requestForm.value.holiday_type_id,
      start_date: requestForm.value.start_date,
      end_date: requestForm.value.end_date,
      working_days_requested: requestForm.value.working_days_requested,
      status: requestForm.value.status,
        approved: requestForm.value.approved,
        notes: requestForm.value.notes,
        requested_by_employee_id: employeeId.value,
      };

      try {
        if (requestEditingId.value) {
          await axios.put(`/holidays/requests/${requestEditingId.value}`, payload);
        } else {
          await axios.post("/holidays/requests", payload);
        }
        requestDialog.value = false;
        await fetchHolidayData();
      } catch (error) {
        console.error("Eroare submitHolidayRequest:", error);
        const message = error?.response?.data?.message || t("employeeHolidays.errors.saveRequest");
        alert(message);
      } finally {
        requestDialogLoading.value = false;
      }
    }

    function formatCurrencyNumber(value) {
      return Number(value || 0).toFixed(2);
    }

    async function submitBalance() {
      if (!employee.value.company_id) {
        alert(t("employeeHolidays.errors.unknownCompany"));
        return;
      }
      balanceDialogLoading.value = true;
      try {
        const payload = {
          company_id: employee.value.company_id,
          employee_id: employeeId.value,
          annual_leave_days: Number(balanceForm.value.annual_leave_days) || 0,
          accrual_start_date: balanceForm.value.accrual_start_date,
          accrual_finish_date: balanceForm.value.accrual_finish_date || null,
          carryover_days: Number(balanceForm.value.carryover_days) || 0,
          total_accrued_days: Number(balanceForm.value.total_accrued_days) || 0,
          total_taken_days: Number(balanceForm.value.total_taken_days) || 0,
          remaining_days: Number(balanceForm.value.remaining_days) || 0,
          notes: balanceForm.value.notes,
          skip_recalc: balanceDialogMode.value === 'edit' ? 1 : 0,
        };
        await axios.post("/holidays/balances", payload);
        balanceDialog.value = false;
        await fetchHolidayData();
      } catch (error) {
        console.error("Eroare submitBalance:", error);
        alert(t("employeeHolidays.errors.saveBalance"));
      } finally {
        balanceDialogLoading.value = false;
        balanceForm.value._editMode = false;
      }
    }

    async function approveRequest(request) {
      if (!canManageRequests.value) return;
      try {
        await axios.post(`/holidays/requests/${request.id}/approve`, {
          approved_by_employee_id: employee.value.id_manager || null,
          notes: request.notes || null,
        });
        await fetchHolidayData();
      } catch (error) {
        console.error("Eroare approveRequest:", error);
        alert(t("employeeDetails.approvals.updateError"));
      }
    }

    async function rejectRequest(request) {
      if (!canManageRequests.value) return;
      try {
        await axios.post(`/holidays/requests/${request.id}/reject`, {
          approved_by_employee_id: employee.value.id_manager || null,
        });
        await fetchHolidayData();
      } catch (error) {
        console.error("Eroare rejectRequest:", error);
        alert(t("employeeDetails.approvals.updateError"));
      }
    }

    async function cancelRequest(request) {
      if (!canManageRequests.value) return;
      try {
        await axios.post(`/holidays/requests/${request.id}/cancel`, {});
        await fetchHolidayData();
      } catch (error) {
        console.error("Eroare cancelRequest:", error);
        alert(t("employeeDetails.approvals.updateError"));
      }
    }

    const fetchEmployeeIdFromProfile = async () => {
      try {
        const { data } = await axios.get("/me");
        const parsedId = parseEmployeeId(data?.employee_id);

        if (parsedId) {
          employeeId.value = parsedId;
          localStorage.setItem("user-employee-id", String(parsedId));
          loggedInEmployeeId.value = parsedId;
        } else {
          employeeId.value = null;
          localStorage.removeItem("user-employee-id");
          loggedInEmployeeId.value = null;
        }

        return parsedId;
      } catch (error) {
        console.error("Eroare la obținerea profilului utilizatorului:", error);
        return null;
      }
    };

    async function fetchEmployee() {
      let parsedId = parseEmployeeId(employeeId.value);
      if (!parsedId) {
        parsedId = await fetchEmployeeIdFromProfile();
      }
      if (!parsedId) {
        employee.value = {};
        return;
      }

      try {
        const response = await axios.get(`/candidati/employees/${parsedId}`);
        employee.value = response.data || {};
      } catch (error) {
        console.error("Eroare la obținerea datelor angajatului:", error);
      }
    }

    async function fetchEmployeesList() {
      try {
        const targetCompanyId = parseCompanyId(
          employee.value?.company_id ?? localStorage.getItem("user-company-id")
        );

        const params = targetCompanyId ? { company_id: targetCompanyId } : {};
        const response = await axios.get("/candidati/employees", {
          params,
        });
        employeesList.value = response.data || [];
      } catch (error) {
        console.error("Eroare la obținerea listei de angajați:", error);
      }
    }

    async function fetchCompanies() {
      try {
        const response = await axios.get("/candidati/companies");
        companiesList.value = response.data || [];
      } catch (error) {
        console.error("Eroare la obținerea companiilor:", error);
      }
    }

    async function fetchHolidayTypes() {
      try {
        const response = await axios.get("/holidays/types");
        holidayTypes.value = response.data || [];
      } catch (error) {
        console.error("Eroare fetchHolidayTypes:", error);
      }
    }

    async function fetchNonWorkingDays() {
      nonWorkingDays.value = [];
      if (!employee.value?.company_id) return;

      const now = new Date();
      let minDate = new Date(now.getFullYear(), 0, 1);
      let maxDate = new Date(now.getFullYear() + 1, 11, 31);

      holidayRequests.value.forEach((req) => {
        const startIso = normalizeDateString(req.start_date);
        const endIso = normalizeDateString(req.end_date);
        const startDate = startIso ? toDateObject(startIso) : null;
        const endDate = endIso ? toDateObject(endIso) : null;
        if (startDate && startDate < minDate) {
          minDate = new Date(startDate);
        }
        if (endDate && endDate > maxDate) {
          maxDate = new Date(endDate);
        }
      });

      try {
        const { data } = await axios.get("/holidays/non-working-days", {
          params: {
            company_id: employee.value.company_id,
            start_date: formatAsIsoDate(minDate),
            end_date: formatAsIsoDate(maxDate),
          },
        });

        nonWorkingDays.value = Array.isArray(data?.occurrences)
          ? data.occurrences
              .map((occ) => ({
                id: Number(occ.id),
                occurrence_date: normalizeDateString(occ.occurrence_date || occ.date),
                name: occ.name || "",
              }))
              .filter((occ) => occ.occurrence_date)
          : [];
      } catch (error) {
        console.error("Eroare fetchNonWorkingDays:", error);
        nonWorkingDays.value = [];
      }
    }

    async function fetchHolidayData() {
      if (!employee.value.company_id) return;
      loadingRequests.value = true;
      try {
        const [balanceRes, requestsRes] = await Promise.all([
          axios.get(`/holidays/balances`, {
            params: { company_id: employee.value.company_id, employee_id: employeeId.value },
          }),
          axios.get(`/holidays/requests`, {
            params: { company_id: employee.value.company_id, employee_id: employeeId.value },
          }),
        ]);
        holidayBalance.value = balanceRes.data?.[0] || null;
        holidayRequests.value = requestsRes.data || [];
        await fetchNonWorkingDays();
      } catch (error) {
        console.error("Eroare fetchHolidayData:", error);
      } finally {
        loadingRequests.value = false;
      }
    }

    const copyText = async (text) => {
      try {
        if (text && navigator?.clipboard?.writeText) {
          await navigator.clipboard.writeText(String(text));
        }
      } catch (e) {
        console.warn("Clipboard copy failed:", e);
      }
    };

    const handlePhoneClick = (value) => copyText(value);
    const handleEmailClick = (value) => copyText(value);

    const loadEmployeeContext = async () => {
      let parsedId = parseEmployeeId(employeeId.value);

      if (!parsedId) {
        parsedId = await fetchEmployeeIdFromProfile();
      }

      if (!parsedId) {
        resetEmployeeState();
        return;
      }

      employeeId.value = parsedId;

      await Promise.all([fetchEmployee(), fetchEmployeesList(), fetchCompanies(), fetchHolidayTypes()]);
      await fetchHolidayData();
      if (showApprovalsSection.value) {
        await processApprovalTokenFromQuery({ refreshAfterSuccess: false });
        await fetchApprovals();
      } else {
        approvalRequests.value = [];
      }
      await fetchChecklistNotifications();
    };

    // Auto-open holiday request dialog when query flag is present
    onMounted(() => {
      if (allowHolidayRequests.value && route.query && route.query.openHolidayRequest) {
        // Wait until holiday types are loaded, then open
        const stop = watch(
          () => holidayTypeOptions.value,
          (list) => {
            if (Array.isArray(list) && list.length) {
              openRequestDialog();
              stop();
            }
          },
          { immediate: true }
        );
      }
    });

    const handleEmployeeIdUpdate = async (nextValue) => {
      const parsedId = parseEmployeeId(nextValue);
      if (parsedId === employeeId.value) {
        return;
      }

      employeeId.value = parsedId;
      resetEmployeeState();

      if (parsedId) {
        await loadEmployeeContext();
      }
    };

    const goToSubordinateApprovals = (id) => {
      router.push({ name: "EmployeeDetails", params: { id } });
    };

    const openSubordinateDetails = (id) => {
      if (route?.name === 'ExternalDashboard') {
        window.dispatchEvent(
          new CustomEvent('ext-dashboard:open-subordinate', { detail: { employeeId: id } })
        );
      } else {
        router.push({ name: "EmployeeDetailsExtView", params: { id } });
      }
    };

    const openChecklistFromNotification = () => {
      window.dispatchEvent(new CustomEvent("ext-dashboard:navigate", { detail: { optionId: "my-checklist" } }));
    };

    onMounted(async () => {
      await loadEmployeeContext();
    });

    watch(
      () => props.employeeId,
      async (nextValue, previousValue) => {
        if (nextValue === previousValue) return;
        await handleEmployeeIdUpdate(nextValue);
      }
    );

    watch(
      () => [route.params.id, route.query.id],
      async ([paramId, queryId], [oldParamId, oldQueryId]) => {
        const nextId = paramId ?? queryId;
        const prevId = oldParamId ?? oldQueryId;
        if (nextId === prevId) return;
        await handleEmployeeIdUpdate(nextId);
      }
    );

    watch(
      showApprovalsSection,
      async (canShow) => {
        if (canShow) {
          await fetchApprovals();
        } else {
          approvalRequests.value = [];
        }
      }
    );

    watch(
      () => employee.value?.company_id,
      async (next, prev) => {
        if (next === prev) return;
        if (showApprovalsSection.value) {
          await fetchApprovals();
        }
        await fetchChecklistNotifications();
      }
    );

    watch(
      () => route.query?.approvalToken,
      async (nextToken, prevToken) => {
        if (!nextToken || nextToken === prevToken) return;
        if (showApprovalsSection.value) {
          await processApprovalTokenFromQuery();
        }
      }
    );

    return {
      employeeId,
      employee,
      employeesList,
      companiesList,
      holidayTypes,
      holidayBalance,
      holidayRequests,
      nonWorkingDays,
      loadingRequests,
      activeVacationTab,
      requestHeaders,
      historyHeaders,
      requestDialog,
      requestDialogLoading,
      requestForm,
      requestEditingId,
      balanceDialog,
      balanceDialogLoading,
      balanceDialogMode,
      balanceForm,
      userRole,
      fullName,
      initials,
      personalInfo,
      professionalInfo,
      statusOptions,
      holidayTypeOptions,
      subordinateEmployees,
      subordinatePendingRequests,
      statistics,
      formatDate,
      formatDateTime,
      formatNumber,
      statusLabel,
      statusChipColor,
      lookupHolidayType,
      openRequestDialog,
      openBalanceDialog,
      submitHolidayRequest,
      submitBalance,
      approveRequest,
      rejectRequest,
      cancelRequest,
      fetchHolidayData,
      handlePhoneClick,
      handleEmailClick,
      canManageRequests,
      goToSubordinateApprovals,
      openSubordinateDetails,
      requestRangeMenu,
      requestRangeActiveEdge,
      allowedCalendarDates,
      calendarEvents,
      rangeDateModel,
      onCalendarRangeChange,
      handleRangePointerDown,
      handleRangeFieldFocus,
      handleRangeKeyboardOpen,
      handleRangeControlClick,
      getWorkingDays,
      totalTakenDisplay,
      displayBalance,
      showAllRequests,
      visibleHolidayRequests,
      goToMyHolidays,
      allowHolidayRequests,
      showBalanceUpdate,
      fillViewport,
      showTopBalanceCard,
      showFluxSummary,
      showTaskNotifications,
      taskNotifications,
      openChecklistFromNotification,
      mainClassObject,
      showApprovalsSection,
      filteredApprovalRequests,
      showApprovalsCard,
      approvalChipLabel,
      approvalChipColor,
      approvalInfoText,
      approveButtonLabel,
      rejectButtonLabel,
      approvalRequests,
      loadingApprovals,
      approvalsError,
      handleApprovalAction,
      approvalActionLoading,
      approvalSnackbar,
      approvalDialog,
      isProcessingApprovalToken,
      truncate,
      t,
    };
  },
});
</script>

<style scoped>
.employee-details-root {
  width: 100%;
  display: block;
}

.employee-main {
  width: 100%;
  padding: 24px 0 32px;
}

.employee-main--compact {
  padding: 0;
}

.employee-details {
  max-width: 1400px;
  margin: 0 auto;
  padding: 0;
}
.hero-card {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  padding: 20px 24px;
  border-radius: var(--app-radius-base) !important;
  gap: 16px;
  /* Standard hero-card pattern — teal left accent + subtle shadow */
  border-left: 4px solid rgb(var(--v-theme-tertiary)) !important;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06) !important;
}

.notification-card {
  border-radius: 16px;
  border: 1px solid rgba(15, 23, 42, 0.08);
  background: linear-gradient(90deg, #f8fbff 0%, #f6f8fc 100%);
}

.notification-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
}

.notification-left {
  display: flex;
  align-items: center;
  gap: 8px;
}

.notification-title {
  font-weight: 700;
  color: #0f172a;
}

.notification-subtitle {
  color: #4b5563;
  font-size: 14px;
}

.notification-stats {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.notif-chip {
  font-weight: 700;
  border-radius: 12px;
}

.hero-left {
  display: flex;
  align-items: center;
  gap: 24px;
}
.hero-avatar {
  font-size: 24px;
  font-weight: 700;
  background: rgba(var(--v-theme-tertiary), 0.12) !important;
  color: rgb(var(--v-theme-tertiary)) !important;
}
.hero-name {
  font-size: 26px;
  font-weight: 600;
}
.hero-meta div {
  font-size: 14px;
  color: var(--app-text-default);
}
.hero-actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  justify-content: flex-end;
}
.column {
  display: flex;
  flex-direction: column;
  gap: 16px;
}
.info-card {
  border-radius: 16px;
}
.approval-summary-card {
  border-radius: 16px;
}
.approval-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.approval-item {
  border: 1px solid #edf2f7;
  border-radius: 12px;
  padding: 8px 12px;
  background: #fff;
  display: grid;
  gap: 6px;
}
.approval-item__row {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  align-items: center;
}
.approval-item__title {
  font-weight: 600;
  font-size: 14px;
}
.approval-item__subtitle {
  font-size: 12px;
  color: #4a5568;
}
.approval-item__meta-row {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  align-items: center;
}
.approval-item__meta {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}
.approval-item__note-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #1a202c;
  font-weight: 600;
  padding: 4px 10px;
  background: #f0f4ff;
  border-radius: 999px;
  max-width: 320px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.approval-item__note-placeholder {
  font-size: 12px;
  color: #718096;
}
.approval-item__actions {
  display: flex;
  gap: 6px;
  flex-wrap: nowrap;
  align-items: center;
}
.info-title {
  font-weight: 600;
  font-size: 16px;
  align-items: center;
}
.info-row {
  display: flex;
  align-items: center;
  font-size: 14px;
  margin-bottom: 6px;
  color: #2d3748;
}
.info-row span {
  margin-left: 6px;
}
.info-row.clickable {
  cursor: pointer;
}
.info-row.clickable span {
  text-decoration: underline;
}
.chips-scroll {
  max-height: 220px;
  overflow-y: auto;
}
.balance-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: 12px;
}
.contact-section {
  margin-top: 12px;
}
.contact-section:first-of-type {
  margin-top: 0;
}
.contact-section h4 {
  margin: 8px 0;
  font-size: 15px;
}
.contact-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.contact-chip-item {
  width: 100%;
  justify-content: flex-start;
}
.request-card {
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 12px;
  margin-bottom: 12px;
}
.request-title {
  font-weight: 600;
  margin-bottom: 4px;
}
.subordinate-card {
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 12px;
  margin-bottom: 12px;
}
.vacation-tabs {
  border-bottom: 1px solid #e2e8f0;
}
@media (max-width: 960px) {
  .hero-card {
    flex-direction: column;
    align-items: flex-start;
    gap: 20px;
    padding: 20px;
  }
  .hero-actions {
    justify-content: flex-start;
  }
}

@media (max-width: 640px) {
  .hero-left {
    width: 100%;
    align-items: flex-start;
    gap: 16px;
  }

  .hero-avatar {
    width: 64px;
    height: 64px;
    font-size: 24px;
  }

  .hero-name {
    font-size: 22px;
  }

  .hero-meta div {
    font-size: 13px;
  }

  .hero-actions {
    width: 100%;
    justify-content: flex-start;
  }

  .hero-actions .v-btn {
    flex: 1 1 auto;
    justify-content: center;
  }
}
</style>
